import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const VERSIONS = [
  {
    version: '1.0.1',
    label: 'Bug fix release',
    date: 'April 20, 2026',
    apk: '/Mytimetable.apk',
    isLatest: true,
    type: 'patch',
    changes: [
      { kind: 'fix', text: 'Fixed validation errors on login and registration forms' },
      { kind: 'fix', text: 'Fixed autofill incorrectly triggering on the department field during registration' },
    ],
  },
  {
    version: '1.0.0',
    label: 'Initial release',
    date: 'April 18, 2026',
    apk: '/Mytimetable-v1.apk',
    isLatest: false,
    type: 'major',
    changes: [
      { kind: 'new', text: 'Smart timetable with automated scheduling' },
      { kind: 'new', text: 'Push notifications for upcoming classes and exams' },
      { kind: 'new', text: 'Course enrollment with personal schedule builder' },
      { kind: 'new', text: 'Email verification and secure login' },
    ],
  },
];

const CHANGE_META = {
  fix: { label: 'Fix', bg: 'rgba(251,146,60,0.10)', border: 'rgba(251,146,60,0.25)', text: '#F97316' },
  new: { label: 'New', bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.22)', text: '#10B981' },
};

const TYPE_META = {
  patch: { label: 'Patch', bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.22)', text: '#10B981' },
  minor: { label: 'Minor', bg: 'rgba(34,211,238,0.08)', border: 'rgba(34,211,238,0.22)', text: '#06B6D4' },
  major: { label: 'Major', bg: 'rgba(83,74,183,0.08)', border: 'rgba(83,74,183,0.22)', text: '#534AB7' },
};

/* ── Mobile card for each version ── */
function VersionCard({ v }) {
  const [open, setOpen] = useState(v.isLatest);
  const type = TYPE_META[v.type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        border: '1px solid #E2E8F0',
        borderRadius: 16,
        overflow: 'hidden',
        background: v.isLatest ? 'rgba(83,74,183,0.025)' : '#FFF',
        boxShadow: v.isLatest ? '0 2px 16px rgba(83,74,183,0.07)' : 'none',
      }}
    >
      {/* Card header */}
      <div
        style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setOpen((p) => !p)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px' }}>
              v{v.version}
            </span>
            {v.isLatest && (
              <span className="pill" style={{ background: 'rgba(83,74,183,0.08)', borderColor: 'rgba(83,74,183,0.22)', color: '#534AB7', fontSize: 9 }}>
                Latest
              </span>
            )}
            <span className="pill" style={{ background: type.bg, borderColor: type.border, color: type.text, fontSize: 9 }}>
              {type.label}
            </span>
          </div>
          <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500 }}>{v.label} · {v.date}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Download icon */}
          <a
            href={v.apk}
            download
            title={`Download v${v.version} APK`}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 9,
              background: v.isLatest ? '#534AB7' : '#F1F5F9',
              color: v.isLatest ? '#FFF' : '#64748B',
              border: v.isLatest ? 'none' : '1px solid #E2E8F0',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a>

          {/* Toggle chevron */}
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.22 }}
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: '#F1F5F9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#64748B', flexShrink: 0,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </motion.div>
        </div>
      </div>

      {/* Changelog */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ borderTop: '1px solid #F1F5F9', padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {v.changes.map((c, ci) => {
                const m = CHANGE_META[c.kind];
                return (
                  <div key={ci} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span className="pill" style={{ background: m.bg, borderColor: m.border, color: m.text, marginTop: 1, flexShrink: 0 }}>
                      {m.label}
                    </span>
                    <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.55 }}>{c.text}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function VersionPage() {
  const [expanded, setExpanded] = useState({ '1.0.1': true, '1.0.0': false });
  const toggle = (v) => setExpanded((prev) => ({ ...prev, [v]: !prev[v] }));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        .vp-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #FFFFFF;
          color: #0F172A;
          min-height: 100vh;
          padding: 80px 5% 0;
          display: flex;
          flex-direction: column;
        }
        .vp-inner { max-width: 1200px; margin: 0 auto; width: 100%; flex: 1; }

        .vp-topnav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 36px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .vp-appname { display: inline-flex; align-items: center; gap: 12px; }
        .vp-nav-actions { display: inline-flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        .vp-home-btn {
          display: inline-flex; align-items: center; gap: 7px;
          background: #F8FAFC; color: #534AB7;
          border: 1px solid #E2E8F0;
          padding: 10px 18px; border-radius: 12px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 700; text-decoration: none;
          transition: transform 0.18s, box-shadow 0.18s, background 0.18s;
        }
        .vp-home-btn:hover { background: #F1F0FF; transform: translateY(-2px); box-shadow: 0 4px 14px rgba(83,74,183,0.12); }

        .vp-download-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: #534AB7; color: #fff;
          padding: 10px 18px; border-radius: 12px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 700; text-decoration: none;
          box-shadow: 0 4px 16px rgba(83,74,183,0.3);
          transition: transform 0.18s, box-shadow 0.18s;
        }
        .vp-download-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(83,74,183,0.4); }

        /* Desktop table */
        .vp-table-wrap {
          overflow-x: auto;
          border-radius: 16px;
          border: 1px solid #E2E8F0;
          box-shadow: 0 2px 16px rgba(83,74,183,0.07);
        }
        .vp-table { width: 100%; border-collapse: collapse; min-width: 560px; }
        .vp-th {
          background: #F8FAFC; border: 1px solid #E2E8F0;
          padding: 10px 16px; font-size: 9px; font-weight: 700;
          color: #CBD5E1; letter-spacing: 0.14em;
          text-align: left; white-space: nowrap; text-transform: uppercase;
        }
        .vp-td { border: 1px solid #E2E8F0; padding: 14px 16px; vertical-align: top; font-size: 13px; }
        .vp-td-meta {
          border: 1px solid #E2E8F0; padding: 14px 16px;
          vertical-align: middle; background: #F8FAFC;
          white-space: nowrap; font-size: 13px;
        }

        .pill {
          display: inline-flex; align-items: center;
          padding: 3px 10px; border-radius: 100px;
          font-size: 10px; font-weight: 700;
          letter-spacing: 0.06em; text-transform: uppercase;
          border: 1px solid; white-space: nowrap;
        }
        .change-row { display: flex; align-items: flex-start; gap: 10px; padding: 5px 0; }
        .toggle-btn {
          background: #F1F5F9; border: none;
          width: 30px; height: 30px; border-radius: 8px;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          color: #64748B; transition: background 0.15s; flex-shrink: 0;
        }
        .toggle-btn:hover { background: #E2E8F0; }
        .vp-dl-icon {
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 9px;
          text-decoration: none; transition: transform 0.15s, box-shadow 0.15s;
        }
        .vp-dl-icon:hover { transform: translateY(-2px); }

        .vp-footer {
          padding: 40px 5%; border-top: 1px solid #F1F5F9;
          text-align: center; color: #94A3B8; font-size: 12px; margin-top: 40px;
        }
        .vp-footer p { margin: 0; letter-spacing: 0.05em; text-transform: uppercase; }
        .vp-footer .sub {
          margin-top: 4px; display: flex; align-items: center;
          justify-content: center; gap: 4px;
          text-transform: none; letter-spacing: normal;
        }
        .vp-footer .version-link {
          margin-top: 8px;
          display: block;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94A3B8;
          text-decoration: none;
          transition: color 0.2s;
        }
        .vp-footer .version-link:hover { color: #64748B; }

        /* Show cards on mobile, table on desktop */
        .vp-cards { display: none; flex-direction: column; gap: 12px; }
        
        @media (max-width: 640px) {
          .vp-root { padding: 60px 4% 0; }
          .vp-topnav { flex-direction: column; align-items: flex-start; gap: 14px; }
          .vp-nav-actions { width: 100%; }
          .vp-home-btn, .vp-download-btn { flex: 1; justify-content: center; font-size: 12px; padding: 10px 14px; }
          .vp-table-wrap { display: none; }
          .vp-cards { display: flex; }
        }
      `}</style>

      <div className="vp-root">
        <div className="vp-inner">

          {/* ── Top nav ── */}
          <motion.div
            className="vp-topnav"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="vp-appname">
              <img src="/unilia_logo.png" alt="Unilia Logo" style={{ height: 32, width: 'auto' }} />
              <span style={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.03em', color: '#003366' }}>
                MyTimetable
              </span>
            </div>

            <div className="vp-nav-actions">
              <a href="/" className="vp-home-btn">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z" />
                  <polyline points="9 21 9 12 15 12 15 21" />
                </svg>
                Home
              </a>
              <a href="/Mytimetable.apk" download className="vp-download-btn">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download App
              </a>
            </div>
          </motion.div>

          {/* ── Page header ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: 28 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <motion.span
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                style={{ width: 5, height: 5, background: '#00D26A', borderRadius: '50%', display: 'inline-block' }}
              />
              <span style={{ color: '#64748B', fontSize: 12, fontWeight: 500 }}>MyTimetable</span>
            </div>
            <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: '#0F172A' }}>Current version:</span>
              <span style={{ color: '#534AB7', fontWeight: 800 }}>v1.0.1</span>
              <span style={{ color: '#CBD5E1' }}>·</span>
              <span style={{ color: '#94A3B8' }}>Requires Android 6.0+</span>
            </div>
          </motion.div>

          {/* ── Desktop table ── */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="vp-table-wrap"
          >
            <table className="vp-table">
              <thead>
                <tr>
                  <th className="vp-th" style={{ width: 160 }}>Version</th>
                  <th className="vp-th" style={{ width: 90 }}>Type</th>
                  <th className="vp-th">Changelog</th>
                  <th className="vp-th" style={{ width: 140 }}>Date</th>
                  <th className="vp-th" style={{ width: 56 }}></th>
                </tr>
              </thead>
              <tbody>
                {VERSIONS.map((v, i) => {
                  const type = TYPE_META[v.type];
                  const isOpen = expanded[v.version];
                  return (
                    <motion.tr
                      key={v.version}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + i * 0.07 }}
                      style={{ background: v.isLatest ? 'rgba(83,74,183,0.025)' : '#FFF' }}
                    >
                      <td className="vp-td-meta">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px' }}>
                              v{v.version}
                            </span>
                            {v.isLatest && (
                              <span className="pill" style={{ background: 'rgba(83,74,183,0.08)', borderColor: 'rgba(83,74,183,0.22)', color: '#534AB7', fontSize: 9 }}>
                                Latest
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500 }}>{v.label}</span>
                        </div>
                      </td>

                      <td className="vp-td-meta">
                        <span className="pill" style={{ background: type.bg, borderColor: type.border, color: type.text }}>
                          {type.label}
                        </span>
                      </td>

                      <td className="vp-td">
                        <div
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, cursor: 'pointer' }}
                          onClick={() => toggle(v.version)}
                        >
                          <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 500 }}>
                            {v.changes.length} change{v.changes.length !== 1 ? 's' : ''}
                          </span>
                          <motion.button
                            className="toggle-btn"
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.22 }}
                            onClick={(e) => { e.stopPropagation(); toggle(v.version); }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </motion.button>
                        </div>

                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                              style={{ overflow: 'hidden' }}
                            >
                              <div style={{ paddingTop: 10, display: 'flex', flexDirection: 'column' }}>
                                {v.changes.map((c, ci) => {
                                  const m = CHANGE_META[c.kind];
                                  return (
                                    <div key={ci} className="change-row">
                                      <span className="pill" style={{ background: m.bg, borderColor: m.border, color: m.text, marginTop: 1 }}>
                                        {m.label}
                                      </span>
                                      <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{c.text}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </td>

                      <td className="vp-td-meta">
                        <span style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>{v.date}</span>
                      </td>

                      <td className="vp-td-meta" style={{ textAlign: 'center' }}>
                        <a
                          href={v.apk}
                          download
                          title={`Download v${v.version} APK`}
                          className="vp-dl-icon"
                          style={{
                            background: v.isLatest ? '#534AB7' : '#F1F5F9',
                            color: v.isLatest ? '#FFF' : '#64748B',
                            border: v.isLatest ? 'none' : '1px solid #E2E8F0',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = v.isLatest
                              ? '0 6px 16px rgba(83,74,183,0.35)'
                              : '0 4px 10px rgba(0,0,0,0.08)';
                          }}
                          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </a>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          {/* ── Mobile cards ── */}
          <div className="vp-cards">
            {VERSIONS.map((v) => (
              <VersionCard key={v.version} v={v} />
            ))}
          </div>

        </div>

        {/* ── Footer ── */}
        <footer className="vp-footer">
          <p>© 2026 Panjie.Dev</p>
          <p className="sub">Built with <span style={{ color: '#EF4444' }}>❤️</span> from Panjie.Dev</p>
        </footer>
      </div>
    </>
  );
}